import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CustomSelect } from "../../../../../../../components/CustomSelect";
import { FlexCenterWrapper } from "../../../../../../../components/DefaultNoDataWrapper";
import Flex from "../../../../../../../components/Flex";
import MaskMultiSelect from "../../../../../../../components/MaskMultiSelect";
import Text from "../../../../../../../components/Text";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { Option } from "../../../../../../../constants/common";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { usePatientVitalsCreate } from "../../../../../../../queries/vitals/use-patient-vitals-create";
import { PatientVitalRequest } from "../../../../../../../services";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../utils/utils";
import AllVitals from "./AllVitals";
import { VitalDateTimeControl } from "./VitalDateTimeControl";
import {
  CustomVitalData,
  INITIAL_VITALS,
  TAKEN_AT_LOCATION_OPTS,
  VITAL_INITIAL_VALUE,
  Vitals,
  VitalsEnumIndex,
  getVitalValidationSchema,
  optionsHere,
} from "./vital-constant";

interface UpsertVitalFormDrawerProps {
  disclosureHost: DisclosureHost;
}

const UpsertVitalFormDrawerNew = ({
  disclosureHost,
}: UpsertVitalFormDrawerProps) => {
  const showSnackbar = useSnackbar();
  const { mutateAsync: createVital } = usePatientVitalsCreate();

  const { patientId } = useParams();
  const queryClient = useQueryClient();

  const [selectedVital, setSelectedVital] = useState<Option[]>();
  const appointmentUuid = useQueryParams("appointmentid");

  const validationSchema = useMemo(() => {
    return getVitalValidationSchema(selectedVital?.map((v) => v.value) || []);
  }, [selectedVital]);

  const formik = useFormik<CustomVitalData>({
    initialValues: VITAL_INITIAL_VALUE,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: handleSubmitVital,
  });

  useEffect(() => {
    formik.setFieldValue("recordedDate", new Date().toISOString());
  }, []);

  function handleSubmitVital(vals: CustomVitalData) {
    if (!validateIsSelectedVital(vals, showSnackbar)) return;

    const patient = { [patientId as string]: "" };

    const payload = {
      patientVitalRequest: { patient, ...vals } as PatientVitalRequest,
      xTENANTID: getXtenantId(),
    };

    showToastAndResetForm(createVital(payload));
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    apiCall
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));

        queryClient.invalidateQueries({
          queryKey: ["patient-vital-list"],
        });
        queryClient.invalidateQueries({
          queryKey: ["patient-latest-vitals-by-patient-id"],
        });
        closeAndReset();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  function closeAndReset() {
    formik.resetForm();
    setSelectedVital(undefined);
    disclosureHost.close();
  }

  const handleActiveVital = (v?: Option[]) => {
    const selectedActiveVitals = formik.values?.patientVitals?.map((it) => {
      const isSelected = v?.some((i) => i?.value === it?.name);

      if (it?.name) {
        return {
          ...(isSelected
            ? it
            : INITIAL_VITALS[VitalsEnumIndex[it.name as Vitals]]),
          selected: isSelected,
          appointmentId: appointmentUuid ?? undefined,
        };
      }
    });

    setSelectedVital(v);
    formik.setFieldValue("patientVitals", selectedActiveVitals);
  };

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndReset}
      dialogTitle="Add Vital"
      dialogAction={
        <StyledButtonV2
          label="Save"
          variant="contained"
          onClick={formik.submitForm}
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "850px",
          maxHeight: "85vh",
        },
      }}
      dialogContentProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <Flex flexDirection={"column"} gap={2} pt={2} pb={2}>
        <MaskMultiSelect
          limitTags={5}
          placeholder="Select Vitals"
          options={optionsHere}
          value={selectedVital}
          onChange={handleActiveVital}
        />

        {formik.values?.patientVitals?.some((v) => v.selected) && (
          <Flex width={"100%"} gap={2}>
            <VitalDateTimeControl formik={formik} />
            <Box width={"150px"}>
              <CustomSelect
                name="TakenAtLocation"
                label="Taken At Location"
                options={TAKEN_AT_LOCATION_OPTS}
                value={formik.values?.takenAtLocation}
                onChange={(v) => formik.setFieldValue("takenAtLocation", v)}
              />
            </Box>
          </Flex>
        )}
      </Flex>

      <Box flex={1} minHeight={0} overflow={"auto"}>
        <Grid container gap={2} alignContent={"flex-start"}>
          {formik.values?.patientVitals?.some((v) => v.selected) ? (
            <AllVitals formik={formik} />
          ) : (
            <FlexCenterWrapper>
              <Text>No Vitals Selected</Text>
            </FlexCenterWrapper>
          )}
        </Grid>
      </Box>
    </StyledFormModal>
  );
};

export default UpsertVitalFormDrawerNew;

const validateIsSelectedVital = (
  vals: CustomVitalData,
  showSnackbar: any
): boolean => {
  const isVitalSelected = vals?.patientVitals?.some((v) => v.selected);
  if (!isVitalSelected) {
    showSnackbar({
      type: "error",
      message: "Please select at least one vital",
    });
    return false;
  }
  return true;
};
